import 'dayjs/locale/tr';
import dayjs from 'dayjs';
import { TICKET_FEEDBACK_STATUS } from 'enums';
import type { FreshdeskFeedbackListItem } from 'library/types';
import { FRESHDESK_RATING_VALUES, MAX_FRESHDESK_RATING, MIN_FRESHDESK_RATING } from './constants';
import type { FeedbackMetrics, RatingTrendPoint, ResponderPerformanceRow, RatingDistributionPoint } from './types';

const round = (value: number, digits = 1) => {
  const multiplier = 10 ** digits;
  return Math.round((value + Number.EPSILON) * multiplier) / multiplier;
};

export const isValidFreshdeskRating = (rate: number | null | undefined): rate is number =>
  typeof rate === 'number' && Number.isInteger(rate) && rate >= MIN_FRESHDESK_RATING && rate <= MAX_FRESHDESK_RATING;

const isAnswered = (item: FreshdeskFeedbackListItem) => item.status === TICKET_FEEDBACK_STATUS.ANSWERED;

type RatedFeedbackItem = FreshdeskFeedbackListItem & { rate: number };

const isRatedFeedback = (item: FreshdeskFeedbackListItem): item is RatedFeedbackItem =>
  isAnswered(item) && isValidFreshdeskRating(item.rate);

const getRatedItems = (items: FreshdeskFeedbackListItem[]) => items.filter(isRatedFeedback);

export const calculateFeedbackMetrics = (items: FreshdeskFeedbackListItem[]): FeedbackMetrics => {
  const answeredCount = items.filter(isAnswered).length;
  const ratedItems = getRatedItems(items);
  const totalRating = ratedItems.reduce((sum, item) => sum + item.rate, 0);
  const satisfiedCount = ratedItems.filter((item) => item.rate >= 4).length;
  const lowRatingCount = ratedItems.filter((item) => item.rate <= 2).length;

  return {
    totalRequests: items.length,
    answeredCount,
    responseRate: items.length ? round((answeredCount / items.length) * 100) : 0,
    averageRating: ratedItems.length ? round(totalRating / ratedItems.length, 2) : 0,
    satisfactionRate: ratedItems.length ? round((satisfiedCount / ratedItems.length) * 100) : 0,
    lowRatingCount,
  };
};

export const buildRatingDistribution = (items: FreshdeskFeedbackListItem[]): RatingDistributionPoint[] => {
  const counts = new Map<number, number>(FRESHDESK_RATING_VALUES.map((rating) => [rating, 0]));

  getRatedItems(items).forEach((item) => {
    counts.set(item.rate, (counts.get(item.rate) || 0) + 1);
  });

  return Array.from(counts, ([rating, count]) => ({ rating, count }));
};

export const buildRatingTrend = (items: FreshdeskFeedbackListItem[], locale: string): RatingTrendPoint[] => {
  const ratedItems = getRatedItems(items)
    .filter((item) => item.updateAt && dayjs(item.updateAt).isValid())
    .sort((left, right) => dayjs(left.updateAt).valueOf() - dayjs(right.updateAt).valueOf());

  if (!ratedItems.length) return [];

  const firstDate = dayjs(ratedItems[0].updateAt);
  const lastDate = dayjs(ratedItems[ratedItems.length - 1].updateAt);
  const spanInDays = lastDate.diff(firstDate, 'day');
  const groupUnit: 'day' | 'week' | 'month' | 'year' =
    spanInDays > 1095 ? 'year' : spanInDays > 180 ? 'month' : spanInDays > 45 ? 'week' : 'day';
  const buckets = new Map<string, { sum: number; count: number; date: dayjs.Dayjs }>();

  ratedItems.forEach((item) => {
    const date = dayjs(item.updateAt).locale(locale);
    const bucketDate = date.startOf(groupUnit);
    const key = bucketDate.format(groupUnit === 'year' ? 'YYYY' : groupUnit === 'month' ? 'YYYY-MM' : 'YYYY-MM-DD');
    const current = buckets.get(key) || { sum: 0, count: 0, date: bucketDate };

    current.sum += item.rate;
    current.count += 1;
    buckets.set(key, current);
  });

  const keyFormat = groupUnit === 'year' ? 'YYYY' : groupUnit === 'month' ? 'YYYY-MM' : 'YYYY-MM-DD';
  const labelFormat = groupUnit === 'year' ? 'YYYY' : groupUnit === 'month' ? 'MMM YY' : 'DD MMM';
  const firstBucketDate = firstDate.locale(locale).startOf(groupUnit);
  const lastBucketDate = lastDate.locale(locale).startOf(groupUnit);
  const trend: RatingTrendPoint[] = [];

  for (
    let bucketDate = firstBucketDate;
    !bucketDate.isAfter(lastBucketDate);
    bucketDate = bucketDate.add(1, groupUnit)
  ) {
    const key = bucketDate.format(keyFormat);
    const bucket = buckets.get(key);

    trend.push({
      key,
      label: bucketDate.format(labelFormat),
      averageRating: bucket ? round(bucket.sum / bucket.count, 2) : null,
      responseCount: bucket?.count ?? 0,
    });
  }

  return trend;
};

export const buildResponderPerformanceRows = (
  items: FreshdeskFeedbackListItem[],
  unknownResponderLabel: string
): ResponderPerformanceRow[] => {
  const groups = new Map<string, FreshdeskFeedbackListItem[]>();

  items.forEach((item) => {
    const key = String(item.responderId);
    const group = groups.get(key);

    if (group) group.push(item);
    else groups.set(key, [item]);
  });

  return Array.from(groups, ([id, responderItems]) => {
    const metrics = calculateFeedbackMetrics(responderItems);
    const latestTimestamp = responderItems.reduce<number | null>((latest, item) => {
      const timestamp = dayjs(item.updateAt).valueOf();
      if (!Number.isFinite(timestamp)) return latest;
      return latest === null || timestamp > latest ? timestamp : latest;
    }, null);
    const firstNamedItem = responderItems.find((item) => item.responderName?.trim());

    return {
      id,
      responderId: responderItems[0]?.responderId ?? 0,
      responderName: firstNamedItem?.responderName?.trim() || unknownResponderLabel,
      totalRequests: metrics.totalRequests,
      answeredCount: metrics.answeredCount,
      responseRate: metrics.responseRate,
      averageRating: metrics.averageRating,
      satisfactionRate: metrics.satisfactionRate,
      lowRatingCount: metrics.lowRatingCount,
      latestUpdateAt: latestTimestamp === null ? null : dayjs(latestTimestamp).toISOString(),
    };
  }).sort(
    (left, right) =>
      right.answeredCount - left.answeredCount ||
      right.averageRating - left.averageRating ||
      left.responderName.localeCompare(right.responderName, 'tr')
  );
};
